import { supabase } from "@/integrations/supabase/client";
import {
  clearPortalSessionToken,
  readPortalSessionToken,
  writePortalSessionToken,
} from "@/lib/portalSessionStorage";

const PIN_CHALLENGE_KEYS = { outlet: "lysara_outlet_pin_challenge", factory: "lysara_factory_pin_challenge" } as const;

export type LoginPortal = "factory" | "outlet" | "other";

export class FacilityPinChallengeExpiredError extends Error {
  constructor() {
    super("Facility PIN verification expired. Authenticate the facility again.");
    this.name = "FacilityPinChallengeExpiredError";
  }
}

export function storePortalPinChallenge(portal: Exclude<LoginPortal, "other">, challenge: string): void {
  try { window.sessionStorage.setItem(PIN_CHALLENGE_KEYS[portal], challenge); } catch { /* unavailable storage */ }
}

export function getPortalPinChallenge(portal: Exclude<LoginPortal, "other">): string | null {
  try { return window.sessionStorage.getItem(PIN_CHALLENGE_KEYS[portal]); } catch { return null; }
}

export function clearPortalPinChallenge(portal: Exclude<LoginPortal, "other">): void {
  try { window.sessionStorage.removeItem(PIN_CHALLENGE_KEYS[portal]); } catch { /* ignore */ }
}

export function clearAllPortalPinChallenges(): void {
  clearPortalPinChallenge("outlet");
  clearPortalPinChallenge("factory");
}

function readSessionToken(): string | null {
  return readPortalSessionToken();
}

function writeSessionToken(token: string): void {
  writePortalSessionToken(token);
}

function clearStoredSessionToken(): void {
  clearPortalSessionToken();
}

/** Replace the user's active login lease while preserving this auth session. */
export async function establishActiveLoginSession(
  portal: LoginPortal,
  facilityId?: string,
): Promise<void> {
  if (portal === "other" || !facilityId) throw new Error("A facility selection is required to open this portal.");
  const challengeId = getPortalPinChallenge(portal);
  if (!challengeId) throw new FacilityPinChallengeExpiredError();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error("No authenticated session");

  const { data, error } = await supabase.functions.invoke("portal-session", {
    body: { action: "establish", portal, facility_id: facilityId, challenge_id: challengeId },
  });
  if (error) {
    const context = (error as { context?: unknown }).context as
      | { clone?: () => { json?: () => Promise<unknown> }; json?: () => Promise<unknown> }
      | undefined;
    let errorMessage: string | null = null;
    if (context && typeof context.json === "function") {
      try {
        const readable = typeof context.clone === "function" ? context.clone() : context;
        const payload = await readable.json?.() as { error?: unknown } | undefined;
        if (typeof payload?.error === "string") errorMessage = payload.error;
      } catch {
        // Keep the challenge for transient/network failures and show a retry message.
      }
    }
    if (errorMessage === "Facility PIN verification expired. Authenticate the facility again.") {
      clearPortalPinChallenge(portal);
      throw new FacilityPinChallengeExpiredError();
    }
    throw new Error(errorMessage || "Could not secure the portal session. Please try again.");
  }
  if (!data?.success || typeof data?.session_token !== "string") {
    throw new Error("Could not secure the portal session. Please try again.");
  }
  writeSessionToken(data.session_token);
}

/** Remove this tab's lease only if it still owns the current lease. */
export async function clearActiveLoginSession(): Promise<void> {
  const token = readSessionToken();
  clearStoredSessionToken();
  if (!token) return;

  const { data, error } = await supabase.from("active_login_sessions").select("portal, facility_id")
    .eq("session_token", token).maybeSingle();
  if (error) throw error;
  if (!data || (data.portal !== "outlet" && data.portal !== "factory") || !data.facility_id) return;
  const { error: revokeError } = await supabase.functions.invoke("portal-session", {
    body: { action: "revoke", portal: data.portal, facility_id: data.facility_id, session_token: token },
  });
  if (revokeError) throw revokeError;
}

export async function isActiveLoginSession(): Promise<boolean> {
  return (await getActiveLoginSessionState()) !== "replaced";
}

export type ActiveLoginSessionState = "active" | "replaced" | "unavailable";

export type ActiveLoginSessionDiagnosis = {
  state: ActiveLoginSessionState;
  reason: "active" | "portal_token_missing" | "supabase_session_missing" | "lease_read_failed" |
    "lease_row_missing" | "lease_token_mismatch" | "lease_context_mismatch";
};

/** Distinguish an invalidated lease from a transient database read failure. */
export async function getActiveLoginSessionState(
  expectedPortal?: Exclude<LoginPortal, "other">,
  expectedFacilityId?: string,
): Promise<ActiveLoginSessionState> {
  return (await diagnoseActiveLoginSession(expectedPortal, expectedFacilityId)).state;
}

/** Return a safe, specific reason for a portal lease check without exposing its token. */
export async function diagnoseActiveLoginSession(
  expectedPortal?: Exclude<LoginPortal, "other">,
  expectedFacilityId?: string,
): Promise<ActiveLoginSessionDiagnosis> {
  const token = readSessionToken();
  if (!token) return expectedPortal
    ? { state: "replaced", reason: "portal_token_missing" }
    : { state: "active", reason: "active" };

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) return { state: "replaced", reason: "supabase_session_missing" };

  const { data, error } = await (supabase.from("active_login_sessions") as any)
    .select("session_token, portal, facility_id")
    .eq("user_id", session.user.id)
    .maybeSingle();

  if (error) return { state: "unavailable", reason: "lease_read_failed" };
  if (!data) return { state: "replaced", reason: "lease_row_missing" };
  if (data.session_token !== token) return { state: "replaced", reason: "lease_token_mismatch" };
  const portal = data.portal;
  const facilityId = data.facility_id;
  if ((portal !== "outlet" && portal !== "factory") || !facilityId) return { state: "replaced", reason: "lease_context_mismatch" };
  if ((expectedPortal && portal !== expectedPortal) || (expectedFacilityId && facilityId !== expectedFacilityId)) {
    return { state: "replaced", reason: "lease_context_mismatch" };
  }
  // RLS permits a user to read only their own row; writes are service-role
  // only, so an exact server-issued token/portal/facility match is authoritative.
  return { state: "active", reason: "active" };
}

export function getStoredActiveLoginSessionToken(): string | null {
  return readSessionToken();
}
